/* eslint-disable complexity */
import api from '@/api/client';
import {getEduAutoInfo} from '@/api/qualify';
import {PIPELINE_TYPE, QUALIFY_STATUS} from '@/dicts/qualify';
import {PipelineType, QualifyInfo} from '@/types/api/qualify';
import {isLoadByIframe} from '@/utils/util';
import {initInfoPassSync, needPassInfo} from './passSync';

function initDisabledAction(actionName: string) {
    let afterAction = '';
    // 从没认证过不允许访问结果页和process页面
    switch (actionName) {
        case 'result':
        case 'processing':
            afterAction = 'index';
            break;
    }
    return afterAction;
}

/**
 * 重定向iframe相关页面不受业务限制
 *
 * @param actionName 跳转页面名称
 * @returns 返回动作名称，如果动作名称是'iframe'，则返回'iframe'，否则返回空字符串
 */
function redirectEscape(actionName: string) {
    if (['iframe', 'sdk'].includes(actionName)) {
        return actionName;
    }
    return '';
}

function redirectAudit(
    status: QUALIFY_STATUS,
    seniorStatus: QUALIFY_STATUS,
    pipelineType: PipelineType,
    actionName: string
) {
    let afterAction = '';
    if (seniorStatus === QUALIFY_STATUS.AUDIT) {
        switch (pipelineType) {
            case PIPELINE_TYPE.TRANSFER_TO_CORPORATION_UNION_PAY:
                afterAction = 'company/remit';
                break;
            case PIPELINE_TYPE.CORPORATION_TO_BAIDU_PAYMENT:
                afterAction = 'company/recharge';
                break;
            default:
                afterAction = 'processing';
                break;
        }
        return afterAction;
    }
    // SUBMIT状态表示刷脸提交
    if (
        status === QUALIFY_STATUS.AUDIT
        && ![QUALIFY_STATUS.PASS, QUALIFY_STATUS.SUBMIT, QUALIFY_STATUS.RETURN].includes(seniorStatus)
    ) {
        afterAction = 'processing';
    }
    // 其他状态不允许访问processing页面
    if (actionName === 'processing' && !afterAction) {
        afterAction = 'result';
    }
    return afterAction;
}

/**
 * 访问资质认证：企业认证失败时和个人认证不允许访问
 * @param {*} status 初级认证失败时不允许访问资质认证
 * @param {*} actionName 跳转路径
 * @returns actionName
 */
function redirectLicense(isPerson: boolean, status: QUALIFY_STATUS, actionName: string) {
    if (actionName.includes('license')) {
        if (!isPerson && status === QUALIFY_STATUS.RETURN) {
            return 'result';
        }
        return actionName;
    }
    return '';
}

export function redirectIndex(info: QualifyInfo, actionName: string) {
    const isInit = !info || (info && !info.person && !info.company);
    if (isInit) {
        return initDisabledAction(actionName) || actionName;
    }
    const {company} = info;
    // 个人认证成功过（初级成功或者高级成功）转企业不允许访问index页面
    const personPassToComp = company && company.person && company.personal_pass;
    // 企业初级通过或者企业高级通过不允许访问个人认证
    const compPrimePass = info.hasPassCorporationPrime || info.company?.enterpriseSeniorStatus === QUALIFY_STATUS.PASS;
    if (actionName === 'index'
        && (personPassToComp || compPrimePass)) {
        return 'result';
    }
    return '';
}

/**
 * 企业初级通过的用户的重定向
 * @param {*} actionName
 * @param {*} seniorStatus
 * @returns
 */
function redirectCompanyPrime(actionName: string, seniorStatus: QUALIFY_STATUS) {
    let afterAction = '';
    // 个人或者企业高级通过，只能跳转到结果页
    const seniorPass = seniorStatus === QUALIFY_STATUS.PASS;
    // 企业初级通过，不允许访问个人认证相关
    const personNoAllow = actionName.includes('person');
    if (seniorPass || personNoAllow) {
        afterAction = 'result';
        return afterAction;
    }
    switch (seniorStatus) {
        case QUALIFY_STATUS.RETURN:
            if (!afterAction && ['index', 'processing'].includes(actionName)) {
                afterAction = 'choices/company';
            }
            afterAction = afterAction || actionName;
            break;
        case QUALIFY_STATUS.NONE:
            afterAction = actionName;
            // 访问企业初级认证自动重定向到认证列表
            if (actionName === 'company' || ['index', 'processing'].includes(actionName)) {
                afterAction = 'choices/company';
            }
            break;
        default:
            afterAction = actionName;
            break;
    }
    return afterAction;
}
/**
 * 个人初级通过的用户的重定向
 * @param {*} actionName
 * @param {*} seniorStatus
 * @returns
 */
function redirectPersonPrime(actionName: string, seniorStatus: QUALIFY_STATUS) {
    let afterAction = '';
    // 个人高级通过，只能跳转到结果页
    const seniorPass = seniorStatus === QUALIFY_STATUS.PASS;
    // 个人初级通过，不允许访问企业认证相关
    const companyNoAllow = actionName.includes('company');
    if (seniorPass || companyNoAllow) {
        afterAction = 'result';
        return afterAction;
    }
    switch (seniorStatus) {
        case QUALIFY_STATUS.RETURN:
            if (!afterAction && ['index', 'processing'].includes(actionName)) {
                afterAction = 'choices/person';
            }
            afterAction = afterAction || actionName;
            break;
        case QUALIFY_STATUS.NONE:
            afterAction = actionName;
            // 个人认证初级通过，未进行高级认证
            afterAction
                = ['index', 'processing', 'company'].includes(actionName) || actionName === 'person'
                    ? 'choices/person'
                    : actionName;
            break;
        default:
            afterAction = actionName;
            break;
    }
    return afterAction;
}

/**
 * 白名单用户初级未通过跳转逻辑（走新实名流程）
 * @param {*} isPerson 是否个人认证
 * @param {*} isPersonPass 个人转企业状态
 * @param {*} actionName 访问页面
 * @param {*} status 初级认证
 * @returns {*} afterAction
 */
function redirectGrayUserNonPrime(
    status: QUALIFY_STATUS,
    isPersonPass: string | null,
    isPerson: boolean,
    actionName: string
) {
    let afterAction = '';
    switch (status) {
        // 初级失败用户
        case QUALIFY_STATUS.RETURN:
            // 企业用户,不允许访问初级认证页面
            if (actionName === 'company' || actionName === 'person') {
                afterAction = 'result';
                return afterAction;
            }
            if (!isPerson) {
                // 个人高级认证转企业失败，不允许访问个人或者企业认证页面
                if (isPersonPass === 'SENIOR') {
                    afterAction = 'result';
                }
                // 个人初级认证转企业失败，不允许访问企业认证页面
                else if (isPersonPass === 'PRIME' && actionName.includes('company')) {
                    afterAction = 'result';
                }
            }
            break;
        default:
            // 不允许访问初级认证页面
            if (actionName === 'company' || actionName === 'person') {
                afterAction = 'result';
            }
            break;
    }

    afterAction = afterAction ? afterAction : actionName;
    return afterAction;
}

function getAuthField(info: QualifyInfo) {
    // 认证信息
    let obj: any = {};
    // 初级认证状态
    let status = null;
    // 高级认证状态
    let seniorStatus = null;
    // 认证方式
    let pipelineType = null;
    // 是否个人认证
    let isPerson = false;
    // 是否个人转企业认证状态，未认证/初级/高级
    let isPersonPass = null;

    // 获取当前的认证类型
    switch (info.type) {
        case 'ENTERPRISE':
            obj = info.company;
            seniorStatus = obj.enterpriseSeniorStatus;
            if (obj.personal_pass && obj.person) {
                isPersonPass = obj.person.gradeStatus === 'SENIOR' ? 'SENIOR' : 'PRIME';
            }
            break;
        case 'PERSONAL':
            obj = info.person;
            // 兼容PASS账号同步高级的情况
            seniorStatus = obj.gradeStatus === 'SENIOR'
                && obj.status === QUALIFY_STATUS.PASS ? QUALIFY_STATUS.PASS : obj.personSeniorStatus;
            isPerson = true;
            break;
    }
    status = obj ? obj.status : '';
    pipelineType = obj ? obj.pipelineType : null;

    return {
        obj,
        status,
        pipelineType,
        isPerson,
        seniorStatus,
        isPersonPass,
    };
}

function getAfterAction(info: QualifyInfo, actionName: string) {
    // 未认证
    const canIndex = redirectIndex(info, actionName);
    if (canIndex) {
        return canIndex;
    }

    // 重定向页面
    let afterAction = '';

    const {status, pipelineType, isPerson, seniorStatus, isPersonPass} = getAuthField(info);
    // 是否跳转到资质认证相关页面
    const canLicense = redirectLicense(isPerson, status, actionName);
    if (canLicense) {
        return canLicense;
    }

    // 检查是否跳转到审核中页面
    const canProcess = redirectAudit(status, seniorStatus, pipelineType, actionName);
    if (canProcess) {
        return canProcess;
    }

    // 初级认证通过用户，存在个人通过转企业
    // 有实名通过hasPassPrime就等于true,个人初级通过转企业失败，hasPassPrime为true
    // 初级status PASS 即可以确定是当前认证方式的初级通过
    if (status === QUALIFY_STATUS.PASS) {
        afterAction = isPerson
            ? redirectPersonPrime(actionName, seniorStatus)
            : redirectCompanyPrime(actionName, seniorStatus);
    }
    else {
        afterAction = redirectGrayUserNonPrime(status, isPersonPass, isPerson, actionName);
    }
    return afterAction;
}

/**
 * 根据认证信息做跳转
 *
 * @param {Object} model 加载完成的model，包含认证信息
 * @param {string} actionName 当前action的名称
 * @return {boolean} 无返回值
 */
export function redirect(info: QualifyInfo, actionName: string) {
    let afterAction = '';
    // 允许PASS实名的时候，只有当前是个人认证才允许，iframe模式下，除了直接访问同步页面，其他情况不自动跳转
    if (info && info.needPassSync && ((actionName.includes('person') && !isLoadByIframe())
        || ['sync'].includes(actionName))) {
        afterAction = 'sync';
    }
    else if (actionName === 'sync') {
        afterAction = 'result';
    }
    else if (redirectEscape(actionName)) {
        afterAction = redirectEscape(actionName);
    }
    else {
        afterAction = getAfterAction(info, actionName);
    }

    // 如果要跳转的页面就是当前页面，则不跳转
    if (afterAction === 'result' && actionName === 'receipt') {
        return '#/404';
    }

    if (actionName !== afterAction) {
        // 使用Location.replace方法跳转页面，达到让浏览器不记录url历史记录的目的
        const afterUrl = afterAction;
        return afterUrl;
    }
    return '';
}


/**
 * 判断是否需要请求passInfo
 * @returns info
 */
export function qualifyInfo() {
    // 合并机主信息查询与学生信息查询
    return Promise.allSettled([api.qualifyInfo(), getEduAutoInfo({silent: true})])
        .then(([qualifyInfo, edu]: any[]) => {
            const info = {
                ...qualifyInfo.value,
                student: edu.value,
            };
            return info;
        }).then(info => {
        if (needPassInfo(info)) {
            return api.passInfo().then(passInfo => initInfoPassSync(info, passInfo));
        }
        return info;
    }).then(async info => {
        info.changeInfo = await api.changeInfo();
        return info;
    });
}
